import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';

export interface ProcessSummaryItem {
  process_id: string;
  process_name: string;
  artifact_name: string;
  item_count: number;
  capacity_utilization: number;
  demand_vs_capacity: number;
  status: string;
  last_transition_date?: string;
}

export interface ProcessSummary {
  process_type: string;
  process_count: number;
  total_items: number;
  processes: ProcessSummaryItem[];
}

export interface ProcessTypeSummary {
  process_type: string;
  processes: ProcessSummaryItem[];
  total_items: number;
  average_utilization: number;
  bottleneck_processes: string[];
}

export interface CapacityMetrics {
  nominal_capacity: number;
  actual_capacity: number;
  planned_capacity: number;
  actual_demand: number;
  target_demand: number;
  utilization_percentage: number;
}

export interface ArtifactItemDetail {
  item_id: string;
  item_name: string;
  current_state: string;
  state_name: string;
  transition_date?: string;
  item_parameters: Record<string, unknown>;
}

export interface ProcessItemsResponse {
  process_id: string;
  process_name: string;
  items: ArtifactItemDetail[];
  item_count: number;
  capacity_metrics: CapacityMetrics;
}

export interface DashboardMetrics {
  total_processes: number;
  total_items: number;
  average_utilization: number;
  bottleneck_count: number;
  process_types: string[];
}

export interface DashboardFilters {
  process_types?: string[];
  process_ids?: string[];
  date_from?: Date;
  date_to?: Date;
}

export class DashboardService extends BaseService {
  static async getProcessSummary(filters?: DashboardFilters): Promise<ProcessSummary[]> {
    const params = new URLSearchParams();

    if (filters?.process_types && filters.process_types.length > 0) {
      filters.process_types.forEach((type) => params.append('process_types', type));
    }

    if (filters?.process_ids && filters.process_ids.length > 0) {
      filters.process_ids.forEach((id) => params.append('process_ids', id));
    }

    if (filters?.date_from) {
      params.append('date_from', filters.date_from.toISOString());
    }

    if (filters?.date_to) {
      params.append('date_to', filters.date_to.toISOString());
    }

    const url = `${API_ENDPOINTS.DASHBOARD_PROCESS_SUMMARY}?${params.toString()}`;
    return this.get<ProcessSummary[]>(url);
  }

  static async getProcessTypeSummary(
    processType: string,
    dateFrom?: Date,
    dateTo?: Date
  ): Promise<ProcessTypeSummary> {
    const params = new URLSearchParams();

    if (dateFrom) {
      params.append('date_from', dateFrom.toISOString());
    }

    if (dateTo) {
      params.append('date_to', dateTo.toISOString());
    }

    const url = `${API_ENDPOINTS.DASHBOARD_PROCESS_TYPE_SUMMARY.replace('{process_type}', processType)}?${params.toString()}`;
    return this.get<ProcessTypeSummary>(url);
  }

  static async getProcessItems(
    processId: string,
    dateFrom?: Date,
    dateTo?: Date
  ): Promise<ProcessItemsResponse> {
    const params = new URLSearchParams();

    if (dateFrom) {
      params.append('date_from', dateFrom.toISOString());
    }

    if (dateTo) {
      params.append('date_to', dateTo.toISOString());
    }

    const url = `${API_ENDPOINTS.DASHBOARD_PROCESS_ITEMS.replace('{process_id}', processId)}?${params.toString()}`;
    return this.get<ProcessItemsResponse>(url);
  }

  static async getAvailableProcessTypes(): Promise<string[]> {
    return this.get<string[]>(API_ENDPOINTS.DASHBOARD_PROCESS_TYPES);
  }

  static async getDashboardMetrics(filters?: DashboardFilters): Promise<DashboardMetrics> {
    const params = new URLSearchParams();

    if (filters?.process_types && filters.process_types.length > 0) {
      filters.process_types.forEach((type) => params.append('process_types', type));
    }

    if (filters?.date_from) {
      params.append('date_from', filters.date_from.toISOString());
    }

    if (filters?.date_to) {
      params.append('date_to', filters.date_to.toISOString());
    }

    const url = `${API_ENDPOINTS.DASHBOARD_METRICS}?${params.toString()}`;
    return this.get<DashboardMetrics>(url);
  }
}
